import ManageSearchRounded from '@mui/icons-material/ManageSearchRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useShop } from '../../shop/ShopProvider';
import type { ReturnHeaderValues } from './schemas';
import { SourcePicker, type SourceKind, type SourceOption } from './SourcePicker';

/** First step of every return/note form: choose the posted document it is made against. */
export function PickSourceCard({
  kind,
  title,
  body,
  onPick,
}: {
  kind: SourceKind;
  title: string;
  body: ReactNode;
  onPick: (o: SourceOption) => void;
}) {
  return (
    <Card sx={{ maxWidth: 640, mx: 'auto', mt: { xs: 1, md: 4 } }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            mb: 2,
            borderRadius: '16px',
            display: 'grid',
            placeItems: 'center',
            color: 'primary.main',
            background:
              'linear-gradient(135deg, color-mix(in srgb, var(--mui-palette-primary-main) 16%, transparent), color-mix(in srgb, var(--mui-palette-primary-main) 4%, transparent))',
            border:
              '1px solid color-mix(in srgb, var(--mui-palette-primary-main) 18%, transparent)',
          }}
        >
          <ManageSearchRounded />
        </Box>
        <Typography variant="h5" sx={{ mb: 0.75 }}>
          {title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {body}
        </Typography>
        <SourcePicker kind={kind} onPick={onPick} autoFocus />
      </CardContent>
    </Card>
  );
}

/** Date, reason and notes of a return or note. */
export function HeaderFields({
  register,
  errors,
  reasonRequired,
  reasonPlaceholder,
}: {
  register: UseFormRegister<ReturnHeaderValues>;
  errors: FieldErrors<ReturnHeaderValues>;
  reasonRequired?: boolean;
  reasonPlaceholder?: string;
}) {
  return (
    <Stack spacing={2}>
      <TextField
        {...register('docDate')}
        type="date"
        label="Date"
        error={Boolean(errors.docDate)}
        helperText={errors.docDate?.message}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        {...register('reason')}
        label={reasonRequired ? 'Reason' : 'Reason (optional)'}
        required={reasonRequired}
        placeholder={reasonPlaceholder}
        error={Boolean(errors.reason)}
        helperText={errors.reason?.message}
      />
      <TextField
        {...register('notes')}
        label="Notes"
        multiline
        minRows={2}
        error={Boolean(errors.notes)}
        helperText={errors.notes?.message}
      />
    </Stack>
  );
}

/** Right-aligned numeric input used in line grids (quantities and amounts). */
export function LineInput({
  value,
  onChange,
  error,
  label,
  suffix,
  prefix,
  width = 132,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
  label: string;
  suffix?: string;
  prefix?: string;
  width?: number;
}) {
  return (
    <TextField
      size="small"
      value={value}
      placeholder="0"
      onChange={(e) => onChange(e.target.value)}
      error={Boolean(error)}
      helperText={error}
      sx={{ width }}
      slotProps={{
        htmlInput: {
          inputMode: 'decimal',
          'aria-label': label,
          style: { textAlign: 'right' },
          className: 'tabular',
        },
        input: {
          startAdornment: prefix ? (
            <InputAdornment position="start" sx={{ mr: 0.25 }}>
              <Typography variant="caption" color="text.disabled">
                {prefix}
              </Typography>
            </InputAdornment>
          ) : undefined,
          endAdornment: suffix ? (
            <InputAdornment position="end" sx={{ ml: 0.25 }}>
              <Typography variant="caption" color="text.disabled">
                {suffix}
              </Typography>
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
}

/** The sticky summary beside a return/note form: figures, an explanation and the submit button. */
export function SummaryPanel({
  title,
  rows,
  total,
  explain,
  children,
  actions,
}: {
  title: string;
  rows: { label: string; value: ReactNode }[];
  total: { label: string; value: ReactNode };
  explain?: ReactNode;
  children?: ReactNode;
  actions: ReactNode;
}) {
  const { format } = useShop();
  return (
    <Card sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {title}
        </Typography>
        {children}
        <Stack spacing={1}>
          {rows.map((r) => (
            <Stack key={r.label} direction="row" sx={{ justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {r.label}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                className="tabular"
                sx={{ fontWeight: 600 }}
              >
                {r.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Divider sx={{ my: 1.75 }} />
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography sx={{ fontWeight: 650 }}>{total.label}</Typography>
          <Typography
            variant="h4"
            className="tabular"
            sx={{ fontWeight: 750 }}
            aria-label={`${total.label} (${format.currencyCode})`}
          >
            {total.value}
          </Typography>
        </Stack>
        {explain && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: '10px',
              bgcolor: 'color-mix(in srgb, var(--mui-palette-info-main) 7%, transparent)',
              border: '1px solid color-mix(in srgb, var(--mui-palette-info-main) 16%, transparent)',
            }}
          >
            <Typography variant="caption" color="text.secondary" component="div">
              {explain}
            </Typography>
          </Box>
        )}
        <Stack direction="row" spacing={1.25} sx={{ mt: 2.5, justifyContent: 'flex-end' }}>
          {actions}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Two-column form layout: lines on the left, summary on the right (stacks below lg). */
export function FormGrid({ main, side }: { main: ReactNode; side: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0, 1fr) 360px' },
        alignItems: 'start',
      }}
    >
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        {main}
      </Stack>
      <Box>{side}</Box>
    </Box>
  );
}

export function GhostButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <Button size="small" onClick={onClick} sx={{ color: 'text.secondary' }}>
      {children}
    </Button>
  );
}
